import React, { ChangeEvent, useState } from 'react';
import { Trans } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';

// Material-UI components
import { Button, CardActions, CardContent, CardHeader, Divider, Grid, TextField, Typography } from '@mui/material';
import { CheckCircle } from '@mui/icons-material';

//@ts-ignore
import { EmptyState } from '@brightlayer-ui/react-components';

import { BrandedCardContainer } from '../../components/BrandedCardContainer/BrandedCardContainer';
import { EMAIL_REGEX, CONTACT_PHONENO } from '../../constants';
import { pushAppRoute } from '../../data/domain/route-manager';
import { useLanguageLocale } from '../../hooks/language-locale-hooks';
import { Thunks } from '../../actions';
import { Selectors } from '../../selectors';

export const ForgotPassword: React.FC = () => {
    const { t } = useLanguageLocale();

    const loading = useSelector(Selectors.Authentication.forgotPasswordLoading);
    const errorMessage = useSelector(Selectors.Authentication.forgotPasswordErrorMessage);
    const success = useSelector(Selectors.Authentication.forgotPasswordSuccess);
    const history = useHistory();

    const [email, setEmail] = useState('');

    const dispatch = useDispatch();

    const validEmail = (address: string): boolean =>
        Boolean(address) && typeof address === 'string' && address.length > 0 && new RegExp(EMAIL_REGEX).test(address);

    const canContinue = (): boolean => validEmail(email) && !loading;

    const goLogin = (): void => {
        pushAppRoute(history, { type: 'Login' });
    };

    const onContinue = (): void => {
        if (success) {
            goLogin();
        } else {
            dispatch(Thunks.Authentication.forgotPassword(email));
        }
    };

    const onBack = (): void => {
        goLogin();
    };

    let body: JSX.Element;
    if (success) {
        body = (
            <div
                style={{ display: 'flex', flex: '1 1 0%', justifyContent: 'center', height: '100%' }}
                data-testid="forgot-password-confirmation-content"
            >
                <EmptyState
                    icon={<CheckCircle style={{ fontSize: 100, marginBottom: 16 }} />}
                    title={t('MESSAGES.EMAIL_SENT')}
                />
            </div>
        );
    } else {
        body = (
            <div data-testid="forgot-password-entry-content">
                <Typography>
                    <Trans i18nKey={'FORGOT_PASSWORD.INSTRUCTIONS'}></Trans>
                    <a href={`tel:${CONTACT_PHONENO}`} style={{ textDecoration: 'none' }}>
                        {CONTACT_PHONENO} .
                    </a>
                </Typography>

                <Divider style={{ margin: '32px 0' }} />

                <TextField
                    label={t('LABELS.EMAIL')}
                    fullWidth
                    id="email"
                    value={email}
                    onChange={(evt: ChangeEvent<HTMLInputElement>): void => setEmail(evt.target.value)}
                    variant="filled"
                    error={Boolean(errorMessage)}
                    helperText={errorMessage ? t('FORGOT_PASSWORD.ERROR') : ''}
                    inputProps={{
                        'data-testid': 'email-input',
                    }}
                />
            </div>
        );
    }

    return (
        <BrandedCardContainer>
            <>
                <CardHeader
                    data-testid="title"
                    title={
                        <Typography variant={'h6'} style={{ fontWeight: 600 }}>
                            {t('HEADER.FORGOT_PASSWORD')}
                        </Typography>
                    }
                />
                <CardContent style={{ flex: '1 1 0px', overflow: 'auto' }}>{body}</CardContent>
                <Divider />
                <CardActions style={{ padding: 16 }}>
                    <Grid
                        container
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        style={{ width: '100%' }}
                    >
                        <Button
                            variant="outlined"
                            color="primary"
                            disabled={success}
                            onClick={onBack}
                            style={{ width: 100 }}
                            data-testid="back-button"
                        >
                            {t('ACTIONS.BACK')}
                        </Button>
                        <Button
                            variant="contained"
                            disabled={!canContinue()}
                            color="primary"
                            onClick={onContinue}
                            style={{ width: 100 }}
                            data-testid="next-button"
                        >
                            {success ? t('ACTIONS.DONE') : t('ACTIONS.OKAY')}
                        </Button>
                    </Grid>
                </CardActions>
            </>
        </BrandedCardContainer>
    );
};
